// FamilyStarsBar — chips for every household member showing total stars
// (all-time, kid chores + adult chores combined). Lets adults see kid
// totals AND their own without bouncing between views. Sits above the
// ApprovalsBanner / Today / All sub-views in the Chores section.
//
// Tap a chip → no-op for v1; a drill-down ("what they did this week")
// can land in a follow-up.

import { useEffect, useState } from "preact/hooks";
import { api, type FamilyStarsMember } from "../../lib/rpc";
import { AdjustStarsModal } from "./adjust-stars-modal";
import "./family-stars-bar.css";

interface Props {
  /** Bumped by the parent on chore mutations so the bar re-fetches. */
  refreshToken: number;
  notify: (msg: string) => void;
}

export function FamilyStarsBar({ refreshToken, notify }: Props) {
  const [members, setMembers] = useState<FamilyStarsMember[] | null>(null);
  const [bankEnabled, setBankEnabled] = useState<boolean>(true);
  const [editing, setEditing] = useState<FamilyStarsMember | null>(null);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      api.householdsStarsByMember().catch(() => [] as FamilyStarsMember[]),
      api.bankConfig().catch(() => ({ rate: 10, enabled: true })),
    ]).then(([m, cfg]) => {
      if (cancelled) return;
      setMembers(m);
      setBankEnabled(cfg.enabled);
    });
    return () => { cancelled = true; };
  }, [refreshToken]);

  if (members === null) return null;          // still loading — hide
  if (members.length === 0) return null;      // empty household — nothing to show

  return (
    <>
      <div className="family-stars-bar" data-testid="family-stars-bar">
        {members.map((m) => (
          <button
            key={m.userId}
            type="button"
            className="family-stars-chip"
            data-kid={m.isKid ? "true" : "false"}
            data-testid={`family-stars-chip-${m.userId}`}
            onClick={() => setEditing(m)}
            aria-label={`Adjust stars for ${m.displayName}`}
          >
            <span
              className="family-stars-avatar"
              style={{ background: m.avatarColor || "var(--accent)" }}
              aria-hidden
            >
              {m.avatarEmoji || initialOf(m.displayName)}
            </span>
            <span className="family-stars-name">{m.displayName}</span>
            <span className="family-stars-count" data-testid={`family-stars-count-${m.userId}`}>
              <span aria-hidden>⭐</span>
              <span>{m.totalStars}</span>
            </span>
            {/* Lifetime — never decreases. Tiny because it's a
                vanity stat, not a balance to act on. */}
            {m.lifetimeStars > m.totalStars && (
              <span
                className="family-stars-lifetime"
                title={`${m.lifetimeStars} stars earned all-time`}
                data-testid={`family-stars-lifetime-${m.userId}`}
              >
                ({m.lifetimeStars} all-time)
              </span>
            )}
            {/* Bank balance pill — only renders if there's a non-zero
                balance so kids without any yet aren't confused. */}
            {bankEnabled && m.dollarsCents > 0 && (
              <span
                className="family-stars-bank"
                data-testid={`family-stars-bank-${m.userId}`}
              >
                <span aria-hidden>🐷</span> ${(m.dollarsCents / 100).toFixed(2)}
              </span>
            )}
          </button>
        ))}
      </div>
      {editing && (
        <AdjustStarsModal
          member={editing}
          notify={notify}
          onClose={() => setEditing(null)}
          onAdjusted={(newTotal) => {
            // Update locally so the chip shows the new total without
            // a full re-fetch. Parent's refreshToken will also rerun
            // the parent's chores query on next mutation.
            setMembers((prev) =>
              prev ? prev.map((m) => m.userId === editing.userId ? { ...m, totalStars: newTotal } : m) : prev,
            );
          }}
        />
      )}
    </>
  );
}

function initialOf(name: string): string {
  const t = name.trim();
  return t ? t.charAt(0).toUpperCase() : "?";
}
